import { createApp } from '@backstage/app-defaults';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_OWNED_BY,
  RELATION_OWNER_OF,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import {
  AppRouter,
  FlatRoutes,
  SignInPageProps,
} from '@backstage/core-app-api';
import {
  AlertDisplay,
  AutoLogout,
  OAuthRequestDialog,
  SignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import {
  appThemeApiRef,
  configApiRef,
  discoveryApiRef,
  githubAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogGraphPage,
  catalogGraphPlugin,
} from '@backstage/plugin-catalog-graph';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { SearchPage } from '@backstage/plugin-search';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import {
  AppShellDefaultLayout,
  LocationContextProvider,
  LocationContextUpdater,
  mbuiDarkThemeWithProvider,
  mbuiLightThemeWithProvider,
  useErrorPageStyle,
  useSearchPageStyle,
} from '@mb.io/core-components';
import {
  odpV2DarkThemeProvider,
  odpV2LightThemeProvider,
} from '@mercedes-benz/odp-theme-v2';
import { HomePage } from '@mercedes-benz/home-v2';
import {
  PlatformFeatureFlagContextProvider,
  PlatformFeatureFlagged,
  PlatformFeatureFlagPage,
  PlatformFeatureSelectedFlagProvider,
  UserFeatureFlagContextProvider,
} from '@mb.io/plugin-platform-feature-flag';
import {
  CatalogAnalyzerPage,
  catalogAnalyzerPlugin,
} from '@mercedes-benz/plugin-catalog-analyzer';
import { DatadogRum } from '@mercedes-benz/plugin-datadog-rum';
import { SecCompPage } from '@mercedes-benz/plugin-seccomp-dashboard';
import { PmasPage } from '@mercedes-benz/pmas';
import { SipCentralPage } from '@mercedes-benz/sip-central';
import { SettingsPage } from '@mercedes-benz/user-settings';
import { PropsWithChildren, useEffect, useState } from 'react';
import { Navigate, Route, useParams } from 'react-router';

import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { apis, oidcAuthRef } from './apis';
import { Root } from './components/Root';
import { EntityPage } from './components/catalog/EntityPage';
import { customDevToolsPage } from './components/dev-tools/CustomDevToolsPage';
import { FetchInterceptorOutlet } from './components/fetch/FetchInterceptorOutlet';
import { ToolboxPage } from '@mercedes-benz/plugin-toolbox';
import { SeamlessIndexPage } from './components/seamless/explorer/SeamlessIndexPage';
import {
  AgentLogoUploadFieldExtension,
  AlphaNumericValidation,
  CatalogEntityNameValidation,
  JavaPackageNameValidation,
  MarkdownField,
  MavenArtifactIdValidation,
  MavenGroupIdValidation,
  NpmArtifactNameValidation,
  NpmScopeValidation,
  SeamlessPrefixValidation,
} from './extensions/extensions';
import './fonts/typeface-mb-corpo-a/index.css';
import './fonts/typeface-mb-corpo-s/index.css';
import * as plugins from './plugins';
import {
  NextScaffolderPage,
  scaffolderPlugin,
} from '@mercedes-benz/scaffolder-next';
import { PostLoginManager } from './components/fetch/PostLoginManager';
import { ICONS } from './icons';
import { AliceErrorPage } from './alice-error.page';
import { DwsPage } from '@mercedes-benz/plugin-dws';
import {
  CatalogHomeApp,
  NewCatalogIndexPage,
} from '@mercedes-benz/plugin-catalog-home-react';
import {
  CatalogV2PageComponent,
  EntityDetailsRoute,
} from '@mercedes-benz/plugin-catalog-v2';
import {
  CommunicationsPage,
  CommsManagerPage,
} from '@mercedes-benz/backstage-plugin-communications-manager';
import {
  DocsReaderPageComponentWithShell,
  TechdocsPage,
  techdocsPlugin as odpTechdocsPlugin,
} from '@mercedes-benz/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  LightBox,
  ReportIssue,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from '@mercedes-benz/backstage-plugin-techdocs-addon-mermaid';
import {
  AcademyPageComponent,
  academyPlugin,
} from '@mercedes-benz/plugin-academy';
import {
  academyContributorRouteRef,
  GettingStartedPage,
  techDocsContributorRouteRef,
} from '@mercedes-benz/backstage-plugin-getting-started';
import { LandingPage } from '@mercedes-benz/plugin-landing-page';
import { SynchronizeTheme } from './components/synchronize-theme/SynchronizeTheme';
import { AiEcosystemPage } from '@mercedes-benz/backstage-plugin-aiecosystem';
import { TdmsNextPage } from '@mercedes-benz/plugin-tdms-next';
import { AuditLogPage } from '@mercedes-benz/backstage-plugin-audit-log';
import { DevClinicPage } from '@mercedes-benz/plugin-devclinic';
import { InsightsHubLandingPage } from '@mercedes-benz/plugin-insights-hub';
import { SearchExplorerPage } from '@mercedes-benz/plugin-search';
import { AppStandardInsightsPage } from '@mercedes-benz/backstage-plugin-app-standard-insights';
import { AdpPage } from '@mercedes-benz/backstage-plugin-adp';
import { GithubMarketplacePage } from '@mercedes-benz/backstage-plugin-github-marketplace';
import { AssetExplorerPage as AssetExplorerPluginPage } from '@mercedes-benz/backstage-plugin-asset-explorer';
import { CaasPage } from '@mercedes-benz/backstage-plugin-caas';
import { EolDashboardPage } from '@mercedes-benz/backstage-endoflife-dashboard';
import { AievaluationPage } from '@mercedes-benz/backstage-plugin-aievaluation';
import { ClmJobsDashboardPage } from '@mercedes-benz/backstage-plugin-clm-jobs-dashboard';
import { PrReviewDashboardPage } from '@mercedes-benz/backstage-plugin-pr-review-dashboard';
import { OdpAdminPage } from '@mercedes-benz/backstage-plugin-odp-admin';
import { FfgPage } from '@mercedes-benz/backstage-plugin-ffg';
import { JourneysHomePage } from '@mercedes-benz/backstage-plugin-journeys';
import { AiChatWidget } from './components/ai-chat/AiChatWidget';
import { DeveloperCompassPage } from '@mercedes-benz/backstage-plugin-developer-compass';

function createSignInComponent() {
  return {
    SignInPage(props: PropsWithChildren<SignInPageProps>) {
      const config = useApi(configApiRef);
      const discoveryApi = useApi(discoveryApiRef);
      const providers: Array<'guest' | SignInProviderConfig> = [];

      const authEnabled = config.getOptionalBoolean('app.auth.enabled');
      const authApi = useApi(oidcAuthRef);
      const origin = authApi.getBackstageIdentity;

      const [aliceError, setAliceError] = useState(false);

      // overriding this function helps us to get exceptions from backend after calling oidc.
      authApi.getBackstageIdentity = async options => {
        try {
          const identity = await origin.apply(authApi, [options]);
          // A stale AutoLogout timestamp would otherwise sign the user out right after an interactive login.
          if (identity && !options?.optional) {
            localStorage.removeItem('@backstage/autologout:lastSeenOnline');
          }
          return identity;
        } catch (e) {
          if (
            e instanceof Error &&
            e.message?.toLowerCase().includes('alice')
          ) {
            setAliceError(true);
            throw new Error('Login failed, invalid role.');
          }
          // Re-throw original error so the real cause is visible
          throw e;
        }
      };
      // explicit false check, if undefined or empty (not configured) or true then the authentication is enabled.
      if (authEnabled === false) {
        // if authentication not provided, enable guest and GitHub provider.
        providers.push('guest');
      } else {
        const enabledProviders: ('oidc' | 'github' | 'guest')[] =
          config.get('app.auth.providers');

        if (enabledProviders.includes('oidc')) {
          providers.push({
            id: 'oidc-auth-provider',
            title: 'GAS OIDC',
            message: 'Sign in using SSO',
            apiRef: oidcAuthRef,
          });
        }

        if (enabledProviders.includes('github')) {
          providers.push({
            id: 'github-auth-provider',
            title: 'GitHub',
            message: 'Sign in using GitHub SSO',
            apiRef: githubAuthApiRef,
          });
        }
      }

      return (
        <>
          <SignInPage
            {...props}
            align="center"
            auto
            providers={providers}
            onSignInSuccess={async identityApi => {
              const url = await discoveryApi.getBaseUrl('auth');
              const manager = new PostLoginManager(identityApi);
              await manager.setTokenCookie(`${url}/session/post-login`);
              props.onSignInSuccess(identityApi);
            }}
          />
          {aliceError && <AliceErrorPage />}
        </>
      );
    },
  };
}

const HomePageRouter = () => {
  const appThemeApi = useApi(appThemeApiRef);
  const [themeId, setThemeId] = useState(appThemeApi.getActiveThemeId());

  useEffect(() => {
    const sub = appThemeApi.activeThemeId$().subscribe(setThemeId);
    return () => sub.unsubscribe();
  }, [appThemeApi]);

  if (themeId?.startsWith('odp-v2-')) {
    return <LandingPage />;
  }
  return <HomePage />;
};

const ExplorePageRouter = () => {
  const appThemeApi = useApi(appThemeApiRef);
  const [themeId, setThemeId] = useState(appThemeApi.getActiveThemeId());

  useEffect(() => {
    const sub = appThemeApi.activeThemeId$().subscribe(setThemeId);
    return () => sub.unsubscribe();
  }, [appThemeApi]);

  if (themeId?.startsWith('odp-v2-')) {
    return <CatalogV2PageComponent />;
  }
  return <ToolboxPage />;
};

// Redirect legacy /user-settings deep links to the new /settings page
const UserSettingsRedirect = () => {
  const { '*': rest } = useParams();
  const suffix = rest ? `/${rest}` : '';
  return <Navigate to={`/settings${suffix}`} replace />;
};

const EsgReaderRedirect = () => {
  const { namespace, kind, name, '*': rest } = useParams();
  if (!namespace || !kind || !name) {
    return <Navigate to="/docs/guidelines" />;
  }
  const suffix = rest ? `/${rest}` : '';
  return <Navigate to={`/docs/${namespace}/${kind}/${name}${suffix}`} />;
};

const EsgCategoryRedirect = () => {
  const { categoryId } = useParams();
  if (!categoryId) {
    return <Navigate to="/docs/guidelines" />;
  }
  return <Navigate to={`/docs/guidelines?categories=${categoryId}`} />;
};

const app = createApp({
  apis,
  themes: [
    mbuiDarkThemeWithProvider,
    mbuiLightThemeWithProvider,
    odpV2DarkThemeProvider,
    odpV2LightThemeProvider,
  ],
  plugins: Object.values(plugins),
  icons: ICONS,
  components: {
    SignInPage: createSignInComponent().SignInPage,
    Router: props => {
      const router = createBrowserRouter([
        { path: '*', Component: () => <>{props.children}</> },
      ]);

      return <RouterProvider router={router} />;
    },
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(catalogGraphPlugin.externalRoutes, {
      catalogEntity: catalogPlugin.routes.catalogEntity,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogAnalyzerPlugin.routes.root,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogAnalyzerPlugin.routes.root,
    });
    bind(academyPlugin.externalRoutes, {
      contributor: academyContributorRouteRef,
    });
    bind(odpTechdocsPlugin.externalRoutes, {
      contributor: techDocsContributorRouteRef,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="/home" />} />
    {/*
     * Rendering route — theme-reactive wrapper picks v2 or classic at runtime.
     * Discovery-only routes below (same path) are permanently shadowed by this
     * one in React Router but are required so Backstage's traverseElementTree
     * registers every routable extension's routeRef from the static element tree.
     */}
    <Route path="/home" element={<HomePageRouter />} />
    <Route path="/home" element={<HomePage />} />
    <PlatformFeatureFlagged feature="odpDesignV2">
      <Route path="/home" element={<LandingPage />} />
    </PlatformFeatureFlagged>

    <PlatformFeatureFlagged feature="odpDesignV2">
      <Route
        path="/catalog/:namespace/:kind/:name"
        element={<EntityDetailsRoute />}
      >
        {EntityPage}
      </Route>
    </PlatformFeatureFlagged>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {EntityPage}
    </Route>
    <Route path="/seamless" element={<SeamlessIndexPage />} />
    <Route
      path="/platform-feature-flag"
      element={<PlatformFeatureFlagPage />}
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<DocsReaderPageComponentWithShell />}
    >
      {/*
       * TechDocs addons must be attached as <Route> children so Backstage's
       * FlatRoutes rewrites them into a catch-all subroute, making them
       * discoverable by useTechDocsAddons() (which relies on useOutlet()).
       * Placing them inside the reader page's JSX has no effect.
       */}
      <TechDocsAddons>
        <ReportIssue />
        <LightBox />
        <Mermaid
          config={{
            theme: 'forest',
            themeVariables: { lineColor: '#000000' },
          }}
        />
      </TechDocsAddons>
    </Route>
    <Route path="/docs/*" element={<TechdocsPage />} />
    <Route path="/esg/category/:categoryId" element={<EsgCategoryRedirect />} />
    <Route
      path="/esg/:namespace/:kind/:name/*"
      element={<EsgReaderRedirect />}
    />
    <Route path="/esg/*" element={<Navigate to="/docs/guidelines" />} />

    <Route path="/create" element={<NextScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <AlphaNumericValidation />
        <CatalogEntityNameValidation />
        <JavaPackageNameValidation />
        <MavenArtifactIdValidation />
        <MavenGroupIdValidation />
        <NpmArtifactNameValidation />
        <NpmScopeValidation />
        <SeamlessPrefixValidation />
        <MarkdownField />
        <AgentLogoUploadFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    {/*
     * Discovery-only route — shadowed at runtime by the route above.
     * Required so traverseElementTree registers catalog:entity routeRef
     * at this path without injecting EntityPage tab extensions.
     */}
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    />
    <PlatformFeatureFlagged feature="odpDesignV2">
      <Route path="/catalog/*" element={<CatalogV2PageComponent />}>
        {NewCatalogIndexPage}
      </Route>
    </PlatformFeatureFlagged>
    <Route
      path="/catalog/*"
      element={
        <AppShellDefaultLayout rootPath="/catalog">
          <CatalogHomeApp />
        </AppShellDefaultLayout>
      }
    />
    {/*
     * Discovery-only route — shadowed at runtime by the routes above.
     * Required so traverseElementTree registers catalog routeRef.
     */}
    <Route path="/catalog" element={<CatalogIndexPage />} />

    <Route path="/explore" element={<ExplorePageRouter />} />
    <Route path="/explore" element={<ToolboxPage />} />
    <Route path="/toolbox" element={<ToolboxPage />} />
    <Route path="/catalog-import" element={<CatalogAnalyzerPage />} />
    <Route path="/search" element={<SearchPage />}>
      <SearchExplorerPage />
    </Route>
    {/*
    FIXME These routes are wrong... sub-routing must be done inside each plugin using the react-router-dom outlet
    **/}
    <Route
      path="/home/communications"
      element={
        <AppShellDefaultLayout rootPath="/home">
          <CommunicationsPage />
        </AppShellDefaultLayout>
      }
    />
    <Route
      path="/home/communications/manager"
      element={
        <AppShellDefaultLayout rootPath="/home">
          <CommsManagerPage />
        </AppShellDefaultLayout>
      }
    />
    {/* Discovery-only route for comms-manager routeRef */}
    <Route path="/home/communications/manager" element={<CommsManagerPage />} />
    <Route
      path="/catalog-graph"
      element={
        <CatalogGraphPage
          initialState={{
            selectedKinds: [
              'component',
              'domain',
              'system',
              'api',
              'group',
              'resource',
              'content',
              'seamless',
              'infrastructure',
              'kubernetes',
            ],
            maxDepth: 1,
            selectedRelations: [
              RELATION_OWNER_OF,
              RELATION_OWNED_BY,
              RELATION_CONSUMES_API,
              RELATION_API_CONSUMED_BY,
              RELATION_PROVIDES_API,
              RELATION_API_PROVIDED_BY,
              RELATION_HAS_PART,
              RELATION_PART_OF,
              RELATION_DEPENDS_ON,
              RELATION_DEPENDENCY_OF,
            ],
          }}
        />
      }
    />
    <Route path="/seccomp" element={<SecCompPage />} />
    <Route path="/settings/*" element={<SettingsPage />} />
    <Route path="/user-settings/*" element={<UserSettingsRedirect />} />
    <Route path="/monitoring-hub" element={<PmasPage />} />
    <Route path="/sip-central" element={<SipCentralPage />} />
    <Route path="/catalog-analyzer" element={<CatalogAnalyzerPage />} />
    <Route path="/tdms-next" element={<TdmsNextPage />} />
    <Route
      path="/audit-log/*"
      element={
        <PlatformFeatureFlagged feature="auditLog">
          <AuditLogPage />
        </PlatformFeatureFlagged>
      }
    />
    <Route
      path="/tdms-next/developer-compass"
      element={<DeveloperCompassPage />}
    />
    <Route
      path="/devtools"
      element={
        <RequirePermission
          permission={devToolsAdministerPermission}
          errorPage={<></>}
        >
          {customDevToolsPage}
        </RequirePermission>
      }
    />
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />
    <Route path="/academy" element={<AcademyPageComponent />} />
    <Route path="/dws" element={<DwsPage />} />
    <Route path="/aiecosystem" element={<AiEcosystemPage />} />
    <Route path="/aievaluation" element={<AievaluationPage />} />
    <Route path="/devclinic" element={<DevClinicPage />} />
    <Route path="/insights-hub/*" element={<InsightsHubLandingPage />} />
    <Route
      path="/app-standard-insights"
      element={<AppStandardInsightsPage />}
    />
    <Route path="/adp" element={<AdpPage />} />
    <Route
      path="/eol-dashboard/*"
      element={
        <PlatformFeatureFlagged feature="endOfLifeDashboard">
          <EolDashboardPage />
        </PlatformFeatureFlagged>
      }
    />
    <Route path="/github-marketplace" element={<GithubMarketplacePage />} />
    <Route path="/asset-explorer/*" element={<AssetExplorerPluginPage />} />
    <Route path="/caas" element={<CaasPage />} />
    <Route path="/home/getting-started" element={<GettingStartedPage />} />
    <Route path="/clm-jobs-dashboard/*" element={<ClmJobsDashboardPage />} />
    <Route
      path="/odp-admin/*"
      element={
        <PlatformFeatureFlagged feature="odpAdminTechdocs">
          <OdpAdminPage />
        </PlatformFeatureFlagged>
      }
    />
    <Route
      path="/pr-review-dashboard/*"
      element={
        <PlatformFeatureFlagged feature="prReviewDashboard">
          <PrReviewDashboardPage />
        </PlatformFeatureFlagged>
      }
    />
    <Route path="/ffg" element={<FfgPage />} />
    <Route path="/journeys" element={<JourneysHomePage />} />
  </FlatRoutes>
);

const AppProvider = app.createRoot(
  <>
    <FetchInterceptorOutlet />
    <SynchronizeTheme />
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <UserFeatureFlagContextProvider>
      <LocationContextProvider>
        <PlatformFeatureFlagContextProvider>
          <PlatformFeatureSelectedFlagProvider>
            <DatadogRum>
              <AutoLogout />
              <AppRouter>
                <Root>
                  <LocationContextUpdater />
                  {routes}
                  <AiChatWidget />
                </Root>
              </AppRouter>
            </DatadogRum>
          </PlatformFeatureSelectedFlagProvider>
        </PlatformFeatureFlagContextProvider>
      </LocationContextProvider>
    </UserFeatureFlagContextProvider>
  </>,
);

const App = () => {
  useErrorPageStyle();
  useSearchPageStyle();
  return <AppProvider />;
};

export default App;
